import { ItemListType, ItemListChildType } from '../../types/pattern';
import { validateFieldCodesInItemList, fieldCodeErrorMessages } from './fieldCode';
import { isChoiceDataType } from '../../constants/itemValidation';
import {
  CALCULATION_FIELD_MAX_COUNT,
  resolveCalculationOrder,
  validateFormula,
} from '../calculation';
import { sanitizeChoices, validateChoices } from './choiceUtils';

/**
 * 総項目数をカウントする関数
 * - 通常項目: 1としてカウント（空欄でもカウント）
 * - 配列項目: 子項目数のみカウント（親はカウントしない、空欄でもカウント）
 *
 * 注意: これは「そのパターンが持つ総項目数」の記録用途（item_count）に使う。
 * 上限チェックには OCR対象のみを数える getOcrTargetItemCount を使うこと（#3045）。
 */
export const getTotalItemCount = (itemList: ItemListType[]): number => {
  let totalCount = 0;
  itemList.forEach(item => {
    if (item.children && item.children.length > 0) {
      // 配列項目: 子項目数のみカウント（親はカウントしない）
      totalCount += item.children.length;
    } else {
      // 通常項目: 1としてカウント
      totalCount += 1;
    }
  });
  return totalCount;
};

/**
 * OCR対象（AI読取する）項目数をカウントする関数（#3045）
 * - OCR対象OFF（is_ocr_target === false）の項目はAI読取しないため上限にカウントしない
 * - 論理削除（is_active === false）の項目もカウントしない
 * - 配列項目: OCR対象の子項目数のみカウント（親はカウントしない）
 * - 通常項目: OCR対象なら1、そうでなければ0
 *
 * 項目数の上限（50件）はOCR精度維持のための制限のため、
 * AI読取する項目だけをこの関数で数えて上限判定する。
 */
export const getOcrTargetItemCount = (itemList: ItemListType[]): number => {
  const isCountTarget = (item: { is_active?: boolean; is_ocr_target?: boolean }): boolean =>
    item.is_active !== false && item.is_ocr_target !== false;

  let totalCount = 0;
  itemList.forEach(item => {
    if (item.children && item.children.length > 0) {
      // 配列項目: OCR対象の子項目数のみカウント（親はカウントしない）
      totalCount += item.children.filter(isCountTarget).length;
    } else if (isCountTarget(item)) {
      // 通常項目: OCR対象なら1
      totalCount += 1;
    }
  });
  return totalCount;
};

/**
 * 項目の型が未選択のものがないかチェックする関数
 */
export const hasInvalidTypes = (itemList: ItemListType[]): boolean => {
  return itemList.some(item => {
    if (!item.type || item.type === '__placeholder__') {
      return true;
    }
    if (item.children && item.children.length > 0) {
      return item.children.some(
        (child: ItemListChildType) => !child.type || child.type === '__placeholder__',
      );
    }
    return false;
  });
};

/**
 * 選択肢型（ラジオボタン・プルダウン）なのに選択肢が未設定・不正な項目を探す（#3075）。
 *
 * 項目リストの行から型だけを変更すると選択肢が空のままになり、
 * その状態で保存するとAIに提示する候補がないままOCRが走ってしまうため、保存前に弾く。
 * 見つからなければ null を返す。
 */
export const findInvalidChoiceItem = (
  itemList: ItemListType[],
): { itemName: string; errorMessage: string } | null => {
  for (const item of itemList) {
    // 無効化された項目は保存されても読取に使われないため対象外
    if (item.is_active === false) continue;

    if (isChoiceDataType(item.type)) {
      const error = validateChoices(sanitizeChoices(item.choices ?? []));
      if (error !== null) {
        return { itemName: item.parentValue, errorMessage: error };
      }
    }

    // 明細（複数行データ）の列も選択肢型（プルダウン）を持てる
    for (const child of item.children ?? []) {
      if (child.is_active === false) continue;
      if (!isChoiceDataType(child.type)) continue;

      const childError = validateChoices(sanitizeChoices(child.choices ?? []));
      if (childError !== null) {
        return { itemName: child.childValue, errorMessage: childError };
      }
    }
  }
  return null;
};

/**
 * 空の項目名があるかチェックする関数
 */
export const hasEmptyItems = (itemList: ItemListType[]): boolean => {
  return itemList.some(item => {
    if (!item.parentValue || item.parentValue.trim() === '') {
      return true;
    }
    if (item.children && item.children.length > 0) {
      return item.children.some(
        (child: ItemListChildType) => !child.childValue || child.childValue.trim() === '',
      );
    }
    return false;
  });
};

/**
 * 項目数制限を超えているかチェックする関数
 * 上限はOCR対象（AI読取する）項目数で判定する（#3045）。
 * OCR対象OFFの項目は上限にカウントしない。
 */
export const isItemCountExceeded = (itemList: ItemListType[]): boolean => {
  const ocrTargetCount = getOcrTargetItemCount(itemList);
  const maxItems = getMaxItemCount();
  return ocrTargetCount > maxItems;
};

/**
 * 項目数制限の上限値を取得する関数
 */
export const getMaxItemCount = (): number => {
  return Number(process.env.REACT_APP_PATTERN_MAX_ITEMS) || 50;
};

/**
 * 項目数超過時のエラーメッセージを生成する関数
 * 上限判定はOCR対象項目数のため、文言もAI読取対象基準であることを明示する（#3045）。
 * @param ocrTargetCount OCR対象（AI読取する）項目数
 */
export const getItemCountExceededMessage = (ocrTargetCount: number): string => {
  const maxItems = getMaxItemCount();
  if (ocrTargetCount === maxItems) {
    return `AI読取する項目数が上限に達しています（現在：${ocrTargetCount}個/${maxItems}個）。`;
  }
  return `AI読取する項目数が上限を超えています（現在：${ocrTargetCount}個/${maxItems}個）。AI読取する項目を${maxItems}個以下に減らしてください。`;
};

// 計算フィールド検証用の項目情報（#3145）
type CalculationFieldEntry = {
  fieldCode: string;
  itemName: string;
  isCalculated: boolean;
  formula: string;
  /** single: 単一項目 / child: テーブル（複数行データ）の子項目 */
  scope: 'single' | 'child';
  /** child のとき、属するテーブルの親項目ID（同じ行の判定に使う） */
  parentId?: string;
};

/** 有効な項目（親・子）を計算フィールド検証用のエントリに展開する */
const collectCalculationEntries = (itemList: ItemListType[]): CalculationFieldEntry[] => {
  const entries: CalculationFieldEntry[] = [];
  itemList.forEach(item => {
    if (item.is_active === false) return;
    if (item.children && item.children.length > 0) {
      item.children.forEach(child => {
        if (child.is_active === false) return;
        entries.push({
          fieldCode: child.field_code ?? '',
          itemName: child.childValue,
          isCalculated: child.is_calculated === true,
          formula: child.formula ?? '',
          scope: 'child',
          parentId: item.id,
        });
      });
    } else {
      entries.push({
        fieldCode: item.field_code ?? '',
        itemName: item.parentValue,
        isCalculated: item.is_calculated === true,
        formula: item.formula ?? '',
        scope: 'single',
      });
    }
  });
  return entries;
};

/**
 * 計算フィールド関連の検証（#3145）。
 * 計算式（構文・参照スコープ・循環参照）と件数上限を検証する。
 * フィールドコード自体の形式・一意性は #3175 の validateFieldCodesInItemList が担う。
 * エラーが無ければ null を返す
 */
export const getCalculationSettingsError = (itemList: ItemListType[]): string | null => {
  const entries = collectCalculationEntries(itemList);

  const calculatedEntries = entries.filter(entry => entry.isCalculated);
  if (calculatedEntries.length === 0) {
    return null;
  }

  if (calculatedEntries.length > CALCULATION_FIELD_MAX_COUNT) {
    return `計算フィールドは1パターンにつき${CALCULATION_FIELD_MAX_COUNT}個までです。`;
  }

  // 計算式はフィールドコードで参照するため、計算フィールドがある場合は全項目にコードが必要
  // （通常は fillMissingFieldCodes で補完済み。ここは最終ガード）
  if (entries.some(entry => entry.fieldCode === '')) {
    return 'フィールドコードが未設定の項目があります。すべての項目にフィールドコードを設定してください。';
  }

  const singleCodes = entries
    .filter(entry => entry.scope === 'single')
    .map(entry => entry.fieldCode);
  const allCodes = entries.map(entry => entry.fieldCode);

  // 参照スコープ: 単一項目は単一項目のみ、子項目は同じ行の子項目と単一項目を参照できる（設計書 §4.4）
  const calculationError = calculatedEntries.reduce<string | null>((found, entry) => {
    if (found) return found;
    if (entry.formula.trim() === '') {
      return `「${entry.itemName}」の計算式を入力してください。`;
    }
    const siblingCodes =
      entry.scope === 'child'
        ? entries
            .filter(other => other.scope === 'child' && other.parentId === entry.parentId)
            .map(other => other.fieldCode)
        : [];
    const result = validateFormula(entry.formula, {
      referenceableFieldCodes: [...singleCodes, ...siblingCodes],
      allFieldCodes: allCodes,
      selfFieldCode: entry.fieldCode,
    });
    if (!result.isValid && result.error) {
      return `「${entry.itemName}」の計算式: ${result.error.message}`;
    }
    return null;
  }, null);
  if (calculationError !== null) {
    return calculationError;
  }

  // 循環参照・依存の深さ（項目間の依存関係全体で検証する）
  const orderResult = resolveCalculationOrder(
    entries.map(entry => ({
      fieldCode: entry.fieldCode,
      formula: entry.isCalculated ? entry.formula : undefined,
    })),
  );
  if (orderResult.error) {
    return `計算式に${orderResult.error.message}`;
  }

  return null;
};

/**
 * 項目リストのバリデーション結果を返す関数
 */
export interface ItemListValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

export const validateItemList = (itemList: ItemListType[]): ItemListValidationResult => {
  const totalCount = getTotalItemCount(itemList);

  if (!itemList || itemList.length === 0 || totalCount === 0) {
    return {
      isValid: false,
      errorMessage: '取得したい項目を最低1つ追加してください。',
    };
  }

  if (isItemCountExceeded(itemList)) {
    return {
      isValid: false,
      // 上限判定・メッセージはOCR対象項目数で行う（#3045）
      errorMessage: getItemCountExceededMessage(getOcrTargetItemCount(itemList)),
    };
  }

  if (hasEmptyItems(itemList)) {
    return {
      isValid: false,
      errorMessage: '項目名が空の項目があります。すべての項目名を入力してください。',
    };
  }

  if (hasInvalidTypes(itemList)) {
    return {
      isValid: false,
      errorMessage: '項目の型を選択してください。',
    };
  }

  // 選択肢型の選択肢が設定されているかのチェック（#3075）
  const choiceError = findInvalidChoiceItem(itemList);
  if (choiceError) {
    return {
      isValid: false,
      errorMessage: `「${choiceError.itemName}」${choiceError.errorMessage}`,
    };
  }

  // フィールドコードの形式・重複チェック（#3175）
  const fieldCodeError = validateFieldCodesInItemList(itemList);
  if (fieldCodeError) {
    return {
      isValid: false,
      errorMessage: `「${fieldCodeError.itemName}」のフィールドコードに問題があります。${fieldCodeErrorMessages[fieldCodeError.error]}`,
    };
  }

  // 計算フィールドの検証（#3145）
  const calculationErrorMessage = getCalculationSettingsError(itemList);
  if (calculationErrorMessage !== null) {
    return {
      isValid: false,
      errorMessage: calculationErrorMessage,
    };
  }

  return {
    isValid: true,
    errorMessage: null,
  };
};
